/* The recognition band's typing stage.
 *
 * Progressive enhancement: Liquid renders every account as an ordinary list, which is
 * what a visitor without JavaScript reads. This script moves that list to
 * `.dp-visually-hidden` and drives the stage instead. Timings are hardcoded from the
 * design's motion table — 45ms per character, a 4.2s hold once complete, four characters
 * removed per tick on the way out.
 *
 * Three things this file is careful about:
 *
 * 1. `prefers-reduced-motion` is checked BEFORE anything is hidden. It used to hide the
 *    list first, so a reduced-motion visitor saw one account where a visitor with no
 *    JavaScript at all saw every one of them — reduced motion was strictly a content-loss
 *    path. Under reduced motion the list simply stays as Liquid rendered it and the stage
 *    is never revealed. The check is live: flipping the OS setting mid-session switches
 *    between the two presentations rather than leaving whichever one loaded.
 *
 * 2. The list is visually hidden, never `hidden`. `hidden` takes it out of the
 *    accessibility tree too, and the stage that replaces it is `aria-hidden` — a
 *    decorative rendering of content that is already on the page — so between them the
 *    accounts would be unreachable to a screen reader.
 *
 * 3. The interval is owned, not fired and forgotten. It exists only while the band is on
 *    screen and the tab is visible, it is cleared on `shopify:section:unload`, and the
 *    `data-dp-recognition-ready` guard stops a second bind stacking a second interval on
 *    the same root — two of them writing `textContent` a tick apart makes the text flicker
 *    between accounts mid-word.
 *
 * The visible pause button this band used to carry was removed on client instruction
 * (recorded in CLAUDE.md, "Accessibility floor"). Pointer hover still pauses the typing,
 * and reduced motion still replaces the stage with the plain list entirely.
 */
(function () {
  'use strict';

  /* Two identical `<script src>` tags would both execute. The `-ready` attribute makes the
     second IIFE's init() a no-op, which is working by accident; this is the theme's actual
     convention (dp-parallax.js, dp-header-morph.js). */
  if (window.dpRecognitionLoaded) return;
  window.dpRecognitionLoaded = true;

  var TICK = 45;
  var HOLD = 4200;
  var ERASE = 4;

  var instances = [];

  function bind(root) {
    if (root.getAttribute('data-dp-recognition-ready') === 'true') return;

    var stage = root.querySelector('[data-dp-recognition-stage]');
    var list = root.querySelector('[data-dp-recognition-list]');
    var metaNode = root.querySelector('[data-dp-recognition-meta]');
    var textNode = root.querySelector('[data-dp-recognition-text]');
    if (!stage || !list || !metaNode || !textNode) return;

    var accounts = Array.prototype.map.call(
      list.querySelectorAll('[data-dp-account]'),
      function (el) {
        return {
          meta: el.getAttribute('data-dp-meta') || '',
          words: (el.textContent || '').trim(),
        };
      }
    );
    if (!accounts.length) return;

    root.setAttribute('data-dp-recognition-ready', 'true');

    var reducedQuery = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

    var timer = null;
    var observer = null;
    var onScreen = true;
    var hovered = false;
    var index = 0;
    var chars = 0;
    var phase = 'type';
    var holdUntil = 0;

    function reduced() {
      return !!(reducedQuery && reducedQuery.matches);
    }

    function paint() {
      metaNode.textContent = accounts[index].meta;
      textNode.textContent = accounts[index].words.slice(0, chars);
    }

    function tick() {
      if (hovered) return;

      var words = accounts[index].words;

      if (phase === 'type') {
        if (chars < words.length) {
          chars += 1;
          paint();
        } else {
          holdUntil = Date.now() + HOLD;
          phase = 'hold';
        }
        return;
      }

      if (phase === 'hold') {
        if (Date.now() > holdUntil) phase = 'erase';
        return;
      }

      if (chars > 0) {
        chars = Math.max(0, chars - ERASE);
        paint();
      } else {
        index = (index + 1) % accounts.length;
        phase = 'type';
        paint();
      }
    }

    function start() {
      if (timer === null) timer = setInterval(tick, TICK);
    }

    function stop() {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    }

    function sync() {
      if (reduced()) {
        stop();
        list.classList.remove('dp-visually-hidden');
        stage.hidden = true;
        return;
      }

      list.classList.add('dp-visually-hidden');
      stage.hidden = false;
      paint();

      if (onScreen && !document.hidden) {
        start();
      } else {
        stop();
      }
    }

    function onVisibility() {
      sync();
    }

    function onEnter() {
      hovered = true;
    }

    function onLeave() {
      hovered = false;
    }

    stage.addEventListener('mouseenter', onEnter);
    stage.addEventListener('mouseleave', onLeave);
    document.addEventListener('visibilitychange', onVisibility);

    if (reducedQuery && reducedQuery.addEventListener) {
      reducedQuery.addEventListener('change', sync);
    }

    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(function (entries) {
        onScreen = entries[entries.length - 1].isIntersecting;
        sync();
      });
      observer.observe(root);
    }

    sync();

    instances.push({
      root: root,
      destroy: function () {
        stop();
        if (observer) observer.disconnect();
        stage.removeEventListener('mouseenter', onEnter);
        stage.removeEventListener('mouseleave', onLeave);
        document.removeEventListener('visibilitychange', onVisibility);
        if (reducedQuery && reducedQuery.removeEventListener) {
          reducedQuery.removeEventListener('change', sync);
        }
      },
    });
  }

  /* `shopify:section:unload` fires only when a section is deleted or hidden. A settings
     change re-renders the section's DOM and fires `load` alone, so the discarded root never
     reaches destroy() — leaking an observer holding a detached node, three listeners, an
     `instances` entry, and, worse than anywhere else in the theme, a live `setInterval`
     still writing `textContent` to nodes that are no longer in the document. One per edit.
     Dropping the detached entries on the way back in is what covers the editor's common
     case; the unload handler covers only delete and hide. */
  function prune() {
    instances = instances.filter(function (instance) {
      if (instance.root.isConnected === false) {
        instance.destroy();
        return false;
      }
      return true;
    });
  }

  function init() {
    prune();
    var roots = document.querySelectorAll('[data-dp-recognition]');
    Array.prototype.forEach.call(roots, bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The theme editor re-renders a section's DOM without re-executing `<script src>`.
     Without these two the band would drop to its raw list after any setting change, and
     the interval bound to the discarded DOM would keep running against detached nodes. */
  document.addEventListener('shopify:section:load', init);

  document.addEventListener('shopify:section:unload', function (event) {
    instances = instances.filter(function (instance) {
      if (event.target.contains(instance.root)) {
        instance.destroy();
        return false;
      }
      return true;
    });
  });
})();
